import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import * as React from "react"

import { TooltipContent } from "@/components/ui/icon-button"
import { SearchInput } from "@/components/ui/search-input"
import { Kbd } from "@/components/ui/stat-card"
import { FilterButton } from "@/components/ui/table-filters"
import { SortButton, type SortOption } from "@/components/ui/table-sort"
import type { FilterDef, FilterValues, TableSort } from "@/lib/table"

export interface TableToolbarProps {
  search: string
  onSearchChange: (v: string) => void
  searchPlaceholder?: string
  searchAriaLabel?: string
  searchRef?: React.RefObject<HTMLInputElement | null>
  /**
   * The key that focuses the search box from anywhere on the page ("/"). Shown inside the box
   * as a keyboard key, with a "Press / to search" tooltip, while the box is empty and not
   * focused — once it is focused the key types itself.
   *
   * This only draws the hint. The page must also call `useSlashShortcut(searchRef)` and pass
   * the same `searchRef`, or the box advertises a key that does nothing.
   */
  searchShortcut?: string

  filterSchema: FilterDef[]
  filterValues: FilterValues
  onFiltersChange: (v: FilterValues) => void
  onClearAllFilters: () => void

  sortOptions: SortOption[]
  sort: TableSort | null
  onSortChange: (v: TableSort | null) => void
}

/**
 * Search left, Filter + Sort right — the shape every table screen uses. Wraps
 * on narrow viewports so search stays full-width.
 */
export function TableToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  searchRef,
  searchShortcut,
  filterSchema,
  filterValues,
  onFiltersChange,
  onClearAllFilters,
  sortOptions,
  sort,
  onSortChange,
}: TableToolbarProps): JSX.Element {
  const inputId = React.useId()
  const [focused, setFocused] = React.useState(false)
  const showShortcut = searchShortcut !== undefined && search.length === 0 && !focused

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="relative min-w-0 flex-1 sm:max-w-[440px]">
        <SearchInput
          ref={searchRef}
          id={inputId}
          value={search}
          onChange={(e) => {
            onSearchChange(e.currentTarget.value)
          }}
          onClear={() => {
            onSearchChange("")
          }}
          onFocus={() => {
            setFocused(true)
          }}
          onBlur={() => {
            setFocused(false)
          }}
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel ?? searchPlaceholder}
          // Screen readers learn the shortcut here, so the visual key below stays hidden from them.
          aria-keyshortcuts={searchShortcut}
        />
        {showShortcut && <SearchShortcutHint inputId={inputId} shortcut={searchShortcut} />}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <FilterButton
          schema={filterSchema}
          values={filterValues}
          onChange={onFiltersChange}
          onClearAll={onClearAllFilters}
        />
        <SortButton options={sortOptions} value={sort} onChange={onSortChange} />
      </div>
    </div>
  )
}

/**
 * The shortcut, drawn as a key rather than a bare "/" (which read as text typed into the
 * box), with a tooltip that says what it does. A label for the box, so clicking the key
 * focuses the search just as pressing it does. Uses the app shell's tooltip provider, so its
 * delay matches every other tooltip and it shares their skip-delay.
 */
function SearchShortcutHint({
  inputId,
  shortcut,
}: {
  inputId: string
  shortcut: string
}): JSX.Element {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        <label
          htmlFor={inputId}
          aria-hidden="true"
          data-testid="search-shortcut"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 cursor-text"
        >
          <Kbd
            keys={shortcut}
            className="border-border-strong border-b-2 bg-surface-sunken text-text-2"
          />
        </label>
      </TooltipPrimitive.Trigger>
      <TooltipContent side="bottom">Press {shortcut} to search</TooltipContent>
    </TooltipPrimitive.Root>
  )
}
